package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{SubjectSessionsRequestData, TranscriptSyncManager, IdentityData, CCABServer => CCABServerNew}
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.AdminPanelController
import com.neurobs.js.dom.{ButtonButton, ElementFactory, InputTextBox}
import com.neurobs.js.dom.ElementFactory.{FileSelectSpan, PanelView, makeForm, makeLink, makeTextInput}
import com.neurobs.js.view.{Container, Form, Gui}
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.util.{Guid, JsonPrinter}
import com.neurobs.js.dom.Grid._
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.react.{DiodeAppExample, ModalContainer}
import org.scalajs.dom.raw.{Event, HTMLElement}
import scalatags.JsDom.all._
import io.circe.generic.auto._
import slinky.web.ReactDOM

import scala.concurrent.ExecutionContext

class AdminMiscController(gui: Gui, user: IdentityData)(implicit executor: ExecutionContext, server: CCABServerNew) extends AdminPanelController {

	private def createTestRunFromLogfile(): Unit = {
		val boxWidth = 40
		val formWidth = 60

		val filenameBox = makeTextInput(boxWidth)
		val form = makeForm(
			width = formWidth,
			title = "Create Test Run From Logfile",
			description = "",
			elements = Array(("Logfile Filename (local):", filenameBox.text)),
			useCancel = true,
			submitCaption = "Create"
		)

		def process(callback: Option[String] => Unit): Either[String, Form.Process] = {
			if (filenameBox.value.isEmpty) Left("Enter a filename.")
			else {
				val (process, response) = server.createTestRunFromLogfile(filenameBox.value)
				response.map {
					case Left(error) => callback(Some(error.description))
					case Right(_) => callback(None)
				}
				Right(process)
			}
		}
		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}

	private def uploadSessionLog(): Unit = {
		val boxWidth = 20
		val formWidth = 60

		val keyIdentifierBox = makeTextInput(boxWidth)
		keyIdentifierBox.value = "session_log"
		val fileBox = new FileSelectSpan
		val form = makeForm(
			width = formWidth,
			title = "Upload Session Log",
			description = "",
			elements = Array(("Key Identifier:", keyIdentifierBox), ("File:", fileBox.element)),
			useCancel = true,
			submitCaption = "Upload"
		)

		def process(callback: Option[String] => Unit): Either[String, Form.Process] = {
			if (fileBox.value.isEmpty) Left("Please select a file.")
			else if (keyIdentifierBox.value.isEmpty) Left("Please enter a key identifier.")
			else {
				val file = fileBox.value.get
				val fileParts = file.name.split("\\.")
				if ( (fileParts.size != 2) || (fileParts(1) != "json") || (fileParts(0).size < 36) )
					Left("The file name must be '[time]_[GUID].json'")
				else Guid(fileParts(0).substring(fileParts(0).size - 36)) match {
					case None => Left("Invalid subject GUID in filename.")
					case Some(guid) =>
						Right(
							CCABServer.uploadSessionLog(subjectGuid = guid.toString, keyIdentifier = keyIdentifierBox.value, file = fileBox.value.get) {
								case Left(error) => callback(Some(error.message))
								case Right(res) => callback(res.error)
							}
						)
				}
			}
		}

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}

	override def container: Container = {
		val panel = new PanelView(title = "Admin Misc", showRefresh = false, fullWidth = true)
		val closeButton = ElementFactory.makeLink(text = "Close")
		closeButton.onclick = (_:Event) => delegate.foreach(_.close())
		panel.setRightCorner(closeButton)

		val createTestRunLink = makeLink(text = "Create Test Run From Logfile")
		createTestRunLink.onclick = _ => createTestRunFromLogfile()
		panel.grid.addRow(createTestRunLink)

		val uploadSessionLogLink = makeLink(text = "Upload Session Log")
		uploadSessionLogLink.onclick = (_) => uploadSessionLog()
		panel.grid.addRow(uploadSessionLogLink)

		def serverInfoSpan(service: String): HTMLElement = {
			val s = span("loading...").render
			CCABServer.serverInfo(service) {
				case Left(e) => s.innerHTML = e.message
				case Right(general) => general.error match {
					case Some(error) => s.innerHTML = error
					case None => s.innerHTML = general.data.get.server_version
				}
			}
			s
		}

		panel.grid.addRow(div("main Service Version: ", serverInfoSpan(service = "main")).render)
		panel.grid.addRow(div("event Service Version: ", serverInfoSpan(service = "event")).render)

		val logfileBox: InputTextBox = makeTextInput(width = 50)
		val testRunStatusButton: ButtonButton = makeButton(caption = "Test Run Status")
		testRunStatusButton.setHandler { () =>
			val logfile = logfileBox.value()
			if (logfile.nonEmpty) {
				CCABServer.getTestRunTranscriptStatus(logfile)(CCABServer.handleObjectResult { res =>
					val message = res match {
						case Left(error) => error
						case Right(status) => status.transcriptSyncedStatus(TranscriptSyncManager.asrEngineIds) +
							"\n\n" + JsonPrinter.jsonPrettyPrint(status.withoutTranscripts)
					}
					gui.showModalFormattedText(width = 75, title = s"Logfile $logfile", content = message)
				})
			}
		}
		panel.grid.addRow(span(logfileBox.text, div(width := 10, display := "inline-block"), testRunStatusButton.button).render)

		val runIdentifierBox: InputTextBox = makeTextInput(width = 50)
		val viewTestRunButton: ButtonButton = makeButton(caption = "View Session/Run")
		viewTestRunButton.setHandler { () =>
			val identifier = runIdentifierBox.value()
			if (identifier.nonEmpty) {
				server.findTestRun(identifier)._2 map {
					case Left(error) => gui.showModalMessage(width = 30, title = "Error", description = error.description, dismissButtonCaption = "Continue")()
					case Right(res) => res.session match {
						case None => gui.showModalMessage(width = 30, title = "Not Found", description = "Unable to find session or test run", dismissButtonCaption = "Continue")()
						case Some(session) =>
							server.getExaminerTags()._2 map {
								case Right(tags) =>
									delegate.foreach(_.newPanel(new SubjectSessionController2(
										gui = gui,
										requestData = SubjectSessionsRequestData(accountId = session.accountId, accountSubjectId = session.accountSubjectId, dataPassword = None),
										userData = user,
										defaultSessionId = Some(session.id),
										defaultTestRunId = res.testRun.map(_.testRun.id),
										titleName = session.accountSubjectId.toString, p => delegate.map(_.newPanel(p)), tags = new ExaminerTagList(tags))))
								case Left(_) =>
							}
					}
				}
			}
		}
		panel.grid.addRow(span(runIdentifierBox.text, div(width := 10, display := "inline-block"), viewTestRunButton.button).render)

		//val testDiv = div().render
		//panel.grid.addRow(testDiv)
		//ReactDOM.render(DiodeAppExample.View(), testDiv)

		panel.container
	}
}
